import { Content, DefaultPage, Footer } from '@shared/layout/DefaultPage'
import { observer } from 'mobx-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthMode } from '@app/providers/authModeProvider'

export const ChatPage = observer(() => {
	const { accessToken: token, initialized } = useAuthMode()
	const wsRef = useRef<WebSocket | null>(null)
	const reconnectTimerRef = useRef<number | null>(null)
	const pingTimerRef = useRef<number | null>(null)
	const reconnectAttemptRef = useRef(0)

	const [connectionState, setConnectionState] = useState<
		'disconnected' | 'connecting' | 'connected' | 'reconnecting'
	>('disconnected')
	const [chatId, setChatId] = useState('')
	const [message, setMessage] = useState('')

	const clearTimers = useCallback(() => {
		if (reconnectTimerRef.current) {
			window.clearTimeout(reconnectTimerRef.current)
			reconnectTimerRef.current = null
		}
		if (pingTimerRef.current) {
			window.clearInterval(pingTimerRef.current)
			pingTimerRef.current = null
		}
	}, [])

	const closeSocket = useCallback(() => {
		const ws = wsRef.current
		wsRef.current = null
		if (!ws) return

		ws.onopen = null
		ws.onmessage = null
		ws.onerror = null
		ws.onclose = null

		try {
			ws.close()
		} catch {
			// ignore
		}
	}, [])

	const connect = useCallback(() => {
		if (!initialized || !token) return
		if (
			wsRef.current &&
			(wsRef.current.readyState === WebSocket.OPEN ||
				wsRef.current.readyState === WebSocket.CONNECTING)
		) {
			return
		}

		clearTimers()
		setConnectionState(prev => (prev === 'connected' ? 'reconnecting' : 'connecting'))

		const ws = new WebSocket(`wss://chat.dev.tisit.club/chat/ws?token=${token}`)
		wsRef.current = ws

		ws.onopen = () => {
			reconnectAttemptRef.current = 0
			setConnectionState('connected')

			// Keep the connection alive behind proxies/load balancers.
			pingTimerRef.current = window.setInterval(() => {
				if (wsRef.current?.readyState !== WebSocket.OPEN) return
				wsRef.current.send(JSON.stringify({ type: 'ping' }))
			}, 25_000)
		}

		ws.onmessage = event => {
			// Server messages are expected to be JSON, but keep it safe.
			try {
				const data = JSON.parse(String(event.data))
				console.log('ws:message', data)
			} catch {
				console.log('ws:message', event.data)
			}
		}

		ws.onerror = error => {
			console.log('ws:error', error)
		}

		ws.onclose = () => {
			clearTimers()
			wsRef.current = null

			setConnectionState('reconnecting')
			if (reconnectTimerRef.current) return

			reconnectAttemptRef.current += 1
			const attempt = reconnectAttemptRef.current
			const delayMs = Math.min(30_000, 500 * 2 ** Math.min(attempt, 6))
			reconnectTimerRef.current = window.setTimeout(() => {
				reconnectTimerRef.current = null
				connect()
			}, delayMs)
		}
	}, [clearTimers, initialized, token])

	useEffect(() => {
		if (!initialized || !token) return
		connect()

		return () => {
			clearTimers()
			closeSocket()
			setConnectionState('disconnected')
		}
	}, [clearTimers, closeSocket, connect, initialized, token])

	const sendUserMessage = useCallback(() => {
		const ws = wsRef.current
		if (!ws || ws.readyState !== WebSocket.OPEN) return
		if (!message.trim()) return

		ws.send(
			JSON.stringify({
				type: 'user_message',
				chat_id: chatId,
				content: message,
				metadata: {},
			})
		)
		setMessage('')
	}, [chatId, message])

	return (
		<DefaultPage noPadding>
			<Content style={{ paddingInline: 15 }}>
				<div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
					ws: {connectionState}
				</div>
				chat
			</Content>
			<Footer
				style={{
					paddingInline: 15,
					paddingTop: 5,
					marginBottom: 'var(--app-bottom-nav-reserve)',
					'--app-page-footer-padding-bottom': '5px',
				}}
			>
				<div style={{ display: 'flex', gap: 8 }}>
					<input
						value={chatId}
						onChange={e => setChatId(e.target.value)}
						placeholder='chat_id'
						style={{
							width: 110,
							padding: '8px 10px',
							borderRadius: 10,
							border: '1px solid rgba(0,0,0,0.15)',
						}}
					/>
					<input
						value={message}
						onChange={e => setMessage(e.target.value)}
						onKeyDown={e => {
							if (e.key === 'Enter') sendUserMessage()
						}}
						placeholder='Message'
						style={{
							flex: 1,
							padding: '8px 10px',
							borderRadius: 10,
							border: '1px solid rgba(0,0,0,0.15)',
						}}
					/>
					<button
						onClick={sendUserMessage}
						disabled={connectionState !== 'connected' || !message.trim()}
						style={{
							padding: '8px 12px',
							borderRadius: 10,
							border: '1px solid rgba(0,0,0,0.15)',
							background:
								connectionState === 'connected' ? '#fff' : 'rgba(0,0,0,0.05)',
						}}>
						Send
					</button>
				</div>
			</Footer>
		</DefaultPage>
	)
})
